// Advanced People Search criteria model.
// Boolean semantics: OR within a field, AND across categories.
// Keywords add explicit required (AND) / any-of groups (OR within, AND across) / excluded (NOT) clauses.

export type Timeframe = 'current' | 'former' | 'both';

export interface KeywordCriteria {
	required: string[];
	anyOfGroups: string[][];
	excluded: string[];
}

interface LanguageCriterion {
	code: string;
	label: string;
	minCefr: number | null;
}

export interface PeopleSearchCriteria {
	role: { titles: string[]; timeframe: Timeframe };
	company: { names: string[]; timeframe: Timeframe };
	location: { values: string[] };
	keywords: KeywordCriteria;
	skills: { names: string[]; mode: 'any' | 'all' };
	languages: { items: LanguageCriterion[] };
	status: { values: string[] };
	lists: { ids: string[] };
	profile: {
		name: string;
		managerIds: string[];
		createdFrom: string | null;
		createdTo: string | null;
	};
}

export type PeopleSearchCategory = keyof PeopleSearchCriteria;

export type PeopleSearchSort = 'newest' | 'oldest' | 'nameAsc' | 'nameDesc';

export const PEOPLE_SEARCH_SORTS: PeopleSearchSort[] = ['newest', 'oldest', 'nameAsc', 'nameDesc'];

export const DEFAULT_PEOPLE_SEARCH_SORT: PeopleSearchSort = 'newest';

// Versioned shape for URL serialization (and future saved-search persistence)
export interface PeopleSearchDefinitionV1 {
	kind: 'people-search-v1';
	criteria: PeopleSearchCriteria;
}

export const PEOPLE_SEARCH_CATEGORIES: PeopleSearchCategory[] = [
	'role',
	'company',
	'location',
	'keywords',
	'skills',
	'languages',
	'status',
	'lists',
	'profile',
];

export const EMPTY_CRITERIA: PeopleSearchCriteria = {
	role: { titles: [], timeframe: 'both' },
	company: { names: [], timeframe: 'both' },
	location: { values: [] },
	keywords: { required: [], anyOfGroups: [], excluded: [] },
	skills: { names: [], mode: 'any' },
	languages: { items: [] },
	status: { values: [] },
	lists: { ids: [] },
	profile: { name: '', managerIds: [], createdFrom: null, createdTo: null },
};

export function cloneCriteria(criteria: PeopleSearchCriteria): PeopleSearchCriteria {
	return JSON.parse(JSON.stringify(criteria)) as PeopleSearchCriteria;
}

export function isCategoryActive(criteria: PeopleSearchCriteria, category: PeopleSearchCategory): boolean {
	switch (category) {
		case 'role':
			return criteria.role.titles.length > 0;
		case 'company':
			return criteria.company.names.length > 0;
		case 'location':
			return criteria.location.values.length > 0;
		case 'keywords':
			return criteria.keywords.required.length > 0
				|| criteria.keywords.anyOfGroups.some(g => g.length > 0)
				|| criteria.keywords.excluded.length > 0;
		case 'skills':
			return criteria.skills.names.length > 0;
		case 'languages':
			return criteria.languages.items.length > 0;
		case 'status':
			return criteria.status.values.length > 0;
		case 'lists':
			return criteria.lists.ids.length > 0;
		case 'profile':
			return criteria.profile.name.trim() !== ''
				|| criteria.profile.managerIds.length > 0
				|| criteria.profile.createdFrom !== null
				|| criteria.profile.createdTo !== null;
	}
}

export function hasActiveCriteria(criteria: PeopleSearchCriteria): boolean {
	return PEOPLE_SEARCH_CATEGORIES.some(category => isCategoryActive(criteria, category));
}

function categoriesDiffer(a: PeopleSearchCriteria, b: PeopleSearchCriteria, category: PeopleSearchCategory): boolean {
	return JSON.stringify(a[category]) !== JSON.stringify(b[category]);
}

export function getDirtyCategories(draft: PeopleSearchCriteria, applied: PeopleSearchCriteria): PeopleSearchCategory[] {
	return PEOPLE_SEARCH_CATEGORIES.filter(category => categoriesDiffer(draft, applied, category));
}

// Stable hash for react-query keys (deterministic because criteria objects are built from EMPTY_CRITERIA shape)
export function criteriaHash(criteria: PeopleSearchCriteria): string {
	return JSON.stringify(criteria);
}

// Chip labels per category for the summary bar (untranslated raw values; suffixes handled by the caller)
export function criteriaChips(criteria: PeopleSearchCriteria): Partial<Record<PeopleSearchCategory, string[]>> {
	const chips: Partial<Record<PeopleSearchCategory, string[]>> = {};

	if (criteria.role.titles.length > 0) {
		const suffix = criteria.role.timeframe === 'former' ? ' (fmr)' : '';
		chips.role = criteria.role.titles.map(v => `${v}${suffix}`);
	}
	if (criteria.company.names.length > 0) {
		const suffix = criteria.company.timeframe === 'former' ? ' (fmr)' : '';
		chips.company = criteria.company.names.map(v => `${v}${suffix}`);
	}
	if (criteria.location.values.length > 0)
		chips.location = [...criteria.location.values];
	if (isCategoryActive(criteria, 'keywords'))
		chips.keywords = [
			...criteria.keywords.required,
			...criteria.keywords.anyOfGroups.filter(g => g.length > 0).map(g => g.join(' / ')),
			...criteria.keywords.excluded.map(v => `−${v}`),
		];
	if (criteria.skills.names.length > 0)
		chips.skills = [...criteria.skills.names];
	if (criteria.languages.items.length > 0)
		chips.languages = criteria.languages.items.map(item =>
			item.minCefr !== null ? `${item.label} ≥ ${item.minCefr}` : item.label);
	if (criteria.status.values.length > 0)
		chips.status = [...criteria.status.values];
	if (criteria.lists.ids.length > 0)
		chips.lists = [...criteria.lists.ids];
	if (isCategoryActive(criteria, 'profile')) {
		const parts: string[] = [];
		if (criteria.profile.name.trim() !== '')
			parts.push(criteria.profile.name);
		if (criteria.profile.managerIds.length > 0)
			parts.push(`+${criteria.profile.managerIds.length}`);
		if (criteria.profile.createdFrom || criteria.profile.createdTo)
			parts.push(`${criteria.profile.createdFrom || '…'} → ${criteria.profile.createdTo || '…'}`);
		chips.profile = parts;
	}

	return chips;
}
